import type { MFE_NAMES } from './constants';
import type { TRouteId } from '$src/generated/routeIds';
import type { TLocale } from '$src/lib/services/locale/types';
import type { TMFEBlock, TStaticBlocks } from '$src/lib/services/staticBlocks';
import type { TMarket } from '$src/lib/services/market/types';

export type TBlockRaw = {
    markup: string;
    styles: string[];
    script: string;
    renderingStrategy: {
        ssr: boolean;
        hydrate: boolean;
    };
    error?: null | {
        message: string;
        stack: string;
    };
};
export type TBlock = {
    mfe: TMFEName;
    module: string;
    payload: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    legacyParams?: any;
    markup: string;
    styles: {
        encoded: string;
        decoded: string[];
    };
    variant?: TVariantBlockData | null;
    scriptSrc: string;
    hydrate: boolean;
};
export type TBlockContext = {
    serverGatekeeper: string;
    // We need to keep globals stuff for backwards compatibility
    globals: {
        market: string;
        config: {
            gameLauncherUrl: string;
            keycloakUrl: string;
            keycloakClientId: string;
            keycloakCookieDomain: string;
            noTrailingSlashUrlsRegex: string;
            countryCodeByIp: string;
        };
        device: {
            isMobile: boolean;
            isDesktop: boolean;
            device: string;
        };
        route: {
            origin: string;
            pathname: string;
            params?: object;
            search: string;
        };
        navigation: {
            operationalSlugs: Record<string, string>;
        };
    };
};

type TMFENamesMap = typeof MFE_NAMES;

export type TMFEName = TMFENamesMap[keyof TMFENamesMap];

export type TMFELocationOverrides = Record<TMFEName, string> | undefined;

export type TLoadMFE = (mfeName: TMFEName, onLoad?: () => void, asModule?: boolean) => void;

export type TWithSlug<T = string> = {
    slug: T;
};

export type TDirectusBlock = {
    component: string;
    props: Record<string, string>;
    mfe: TWithSlug<TMFEName>;
};

export type TDirectusBlocksPerMarket = {
    languages_code: {
        code: TLocale;
    };
    market_common_blocks: TDirectusBlock[];
    market_los_only_blocks: TDirectusBlock[];
    market_lis_only_blocks: TDirectusBlock[];
};

export type TDirectusStaticBlocks = {
    excluded_page_groups: TWithSlug<TRouteId>[];
    common_blocks: TDirectusBlock[];
    los_only_blocks: TDirectusBlock[];
    lis_only_blocks: TDirectusBlock[];
    blocks_per_market: TDirectusBlocksPerMarket[];
};

export type TMappedDirectusStaticBlocks = {
    commonBlocks: Array<TMFEBlock>;
    losOnlyBlocks: Array<TMFEBlock>;
    lisOnlyBlocks: Array<TMFEBlock>;
};

export type TMappedStaticBlocks = {
    excludedPageGroups: Array<TRouteId>;
    blocks: TMappedDirectusStaticBlocks;
};

export type TStaticBlocksMapped = {
    [key in keyof TMappedDirectusStaticBlocks]: TStaticBlocks;
};

export type TGetDirectusStaticBlocksParams = {
    market: TMarket;
    isProduction: boolean;
    fetch?: typeof globalThis.fetch;
    mapStaticBlocks: (blocks: TMappedDirectusStaticBlocks) => Promise<TStaticBlocksMapped>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TConditionalBlockCheck = <T = any>(
    operation: BlockConditionOperator,
    expected: T,
    actual: T
) => boolean;

export enum BlockConditionOperator {
    EQ = 'eq'
}
export enum BlockConditionProperty {
    PlayerCountry = 'player_country'
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TBlockCondition<V = any> = {
    property: BlockConditionProperty;
    operator: BlockConditionOperator;
    value: V; // allow any value passed in
};

export type TVariantBlockData = {
    name: string;
    condition: TBlockCondition;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TGetCurrentPropertyValue = <T = any>(property: BlockConditionProperty) => T;
